import React from 'react';
import { typeLabel } from './questions';

/**
 * AnswersView — what a decision model answered: per question the outcome,
 * its confidence and a bar per possible answer. `questions` is the API map,
 * `answers` the normalised answers from /decision-models/decide (or an eval
 * step's details.answers).
 */

const pct = (p) => (typeof p === 'number' ? `${Math.round(p * 100)}%` : '—');

function Bar({ label, p, chosen }) {
  const w = typeof p === 'number' ? Math.max(0, Math.min(1, p)) * 100 : 0;
  return (
    <div className="grid grid-cols-[minmax(0,140px)_1fr_40px] items-center gap-2 text-[12px]">
      <span className={`truncate ${chosen ? 'font-medium text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`} title={label}>{label}</span>
      <span className="h-1.5 overflow-hidden rounded-full bg-[color:var(--surface-sunken)]">
        <span
          className="block h-full rounded-full"
          style={{ width: `${w}%`, backgroundColor: chosen ? 'var(--trodo-primary)' : 'var(--text-disabled)' }}
        />
      </span>
      <span className="text-right tabular-nums text-[color:var(--text-muted)]">{pct(p)}</span>
    </div>
  );
}

function outcomeText(q, a) {
  if (!a) return '—';
  if (a.outcome === 'cant_tell') return "Can't tell";
  if (a.type === 'noul') return a.outcome === 'yes' ? 'Yes' : 'No';
  if (a.type === 'score') {
    const v = typeof a.value === 'number' ? a.value : Number(a.outcome);
    const level = a.level != null ? a.level : Math.round(v);
    const label = a.label || (q && Array.isArray(q.criteria) ? q.criteria[level] : '');
    return `${Number.isInteger(v) ? v : v.toFixed(2)}${label ? ` · ${label}` : ''}`;
  }
  return a.outcome;
}

function bars(q, a) {
  const probs = a.probabilities || {};
  if (a.type === 'noul') {
    const p = typeof a.p_yes === 'number' ? a.p_yes : probs.yes;
    return [
      { key: 'yes', label: 'Yes', p, chosen: a.outcome === 'yes' },
      { key: 'no', label: 'No', p: typeof p === 'number' ? 1 - p : probs.no, chosen: a.outcome === 'no' },
    ];
  }
  if (a.type === 'choice') {
    const keys = q && q.criteria ? Object.keys(q.criteria) : Object.keys(probs);
    return keys.map((k) => ({ key: k, label: k, p: probs[k], chosen: a.outcome === k }));
  }
  const levels = q && Array.isArray(q.criteria) ? q.criteria : Object.keys(probs);
  const level = a.level != null ? a.level : Math.round(Number(a.value ?? a.outcome));
  return levels.map((l, i) => ({ key: String(i), label: `${i} · ${l}`, p: probs[String(i)], chosen: level === i }));
}

export default function AnswersView({ questions, answers }) {
  const keys = Object.keys(questions || answers || {});
  return (
    <div className="space-y-2" data-testid="decision-answers">
      {keys.map((k) => {
        const q = questions ? questions[k] : null;
        const a = answers ? answers[k] : null;
        return (
          <section key={k} className="rounded-lg border border-[color:var(--border-default)] bg-surface px-3 py-2.5">
            <div className="flex items-baseline justify-between gap-2">
              <div className="min-w-0">
                <span className="font-mono text-[12px] font-medium text-[color:var(--text-primary)]">{k}</span>
                <span className="ml-2 text-[11px] text-[color:var(--text-muted)]">{typeLabel((a && a.type) || (q && q.type))}</span>
              </div>
              <div className="shrink-0 text-right text-[12px]">
                <span className="font-medium text-[color:var(--text-primary)]">{outcomeText(q, a)}</span>
                {a && typeof a.confidence === 'number' && (
                  <span className="ml-2 tabular-nums text-[color:var(--text-muted)]">{pct(a.confidence)} sure</span>
                )}
              </div>
            </div>
            {a && (
              <div className="mt-2 space-y-1">
                {bars(q, a).map((b) => <Bar key={b.key} label={b.label} p={b.p} chosen={b.chosen} />)}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
